import { useEffect, useState } from "react";

import { faLock, faLockOpen } from "@fortawesome/pro-regular-svg-icons";
import { isNil } from "lodash-es";

import { IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { useRevealEntityField } from "src/entities/queries";
import {
  SensitiveField,
  SensitiveFieldValue,
} from "src/entitiesEventsCommon/SensitiveField";
import { useRevealEventField } from "src/eventsCatalogue/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment } from "src/hooks/useEnvironment";
import { toastError } from "src/utils/toastError";

const REVEAL_TOOLTIP_BODY =
  "Unmask the sensitive field. A log of this action will be recorded in the Audit Log. The field will revert back to being masked when you leave the page";

const revealedState = (
  isRevealed: boolean,
  revealData: { value: unknown } | undefined,
  value: unknown,
) => ({
  isRevealed: isRevealed && !!revealData,
  value: isRevealed && revealData ? revealData.value : value,
});

const useResetOnRevealError = (
  revealError: unknown,
  setRevealedTarget: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  useEffect(() => {
    if (!revealError) return;

    setRevealedTarget(null);
    toastError(revealError, { title: "Could not reveal masked field" });
  }, [revealError, setRevealedTarget]);
};

const useRevealStateForTarget = (targetKey: string) => {
  const [revealedTarget, setRevealedTarget] = useState<string | null>(null);
  const isRevealed = revealedTarget === targetKey;

  if (revealedTarget !== null && !isRevealed) {
    setRevealedTarget(null);
  }

  return {
    isRevealed,
    setRevealedTarget,
    toggleRevealed: () => setRevealedTarget(isRevealed ? null : targetKey),
  };
};

type SensitivePropertyFieldViewProps = {
  propertyName: string;
  value: unknown;
  displayJsonFormat?: boolean;
  isRevealable: boolean;
  isRevealed: boolean;
  isLoading: boolean;
  onToggle: () => void;
};

const SensitivePropertyFieldView: React.FC<SensitivePropertyFieldViewProps> = ({
  propertyName,
  value,
  displayJsonFormat,
  isRevealable,
  isRevealed,
  isLoading,
  onToggle,
}) => (
  <div className="flex w-full items-center gap-x-1">
    <div className="min-w-0 flex-1 whitespace-normal break-words">
      {isLoading ? (
        <Skeleton height="h-4" width="w-32" />
      ) : (
        <SensitiveField
          displayInfoIcon={!isRevealable}
          displayJsonFormat={displayJsonFormat}
          isRevealed={isRevealed}
          propertyName={propertyName}
          value={value as SensitiveFieldValue}
        />
      )}
    </div>
    {!isNil(value) && isRevealable && !isLoading && (
      <Tooltip
        body={!isRevealed && REVEAL_TOOLTIP_BODY}
        placement="right"
        title={isRevealed ? "Hide sensitive data" : "Reveal masked field"}
        asChild
      >
        <IconButton
          dataLoc="reveal-masked-field"
          icon={isRevealed ? faLockOpen : faLock}
          size="xs"
          onClick={onToggle}
        />
      </Tooltip>
    )}
  </div>
);

type SensitiveEntityPropertyFieldProps = {
  env: Environment;
  propertyName: string;
  value: unknown;
  schemaId: string;
  entityId: string;
  field: string;
  displayJsonFormat?: boolean;
};

export const SensitiveEntityPropertyField: React.FC<
  SensitiveEntityPropertyFieldProps
> = ({
  env,
  propertyName,
  value,
  schemaId,
  entityId,
  field,
  displayJsonFormat,
}) => {
  const { sensitiveFields } = useCapabilities();
  const { isRevealed, setRevealedTarget, toggleRevealed } =
    useRevealStateForTarget(`${schemaId}:${entityId}:${field}`);

  const hasTarget = !!(schemaId && entityId);

  const {
    data: revealData,
    error: revealError,
    isLoading,
  } = useRevealEntityField({
    env,
    schemaId,
    entityId,
    field,
    enabled: isRevealed && hasTarget,
  });

  useResetOnRevealError(revealError, setRevealedTarget);

  return (
    <SensitivePropertyFieldView
      displayJsonFormat={displayJsonFormat}
      isLoading={isLoading}
      isRevealable={hasTarget && sensitiveFields.canReveal}
      propertyName={propertyName}
      onToggle={toggleRevealed}
      {...revealedState(isRevealed, revealData, value)}
    />
  );
};

type SensitiveEventPropertyFieldProps = {
  env: Environment;
  propertyName: string;
  value: unknown;
  eventType: string;
  eventId: string;
  field: string;
  timestamp: string | null;
  displayJsonFormat?: boolean;
};

export const SensitiveEventPropertyField: React.FC<
  SensitiveEventPropertyFieldProps
> = ({
  env,
  propertyName,
  value,
  eventType,
  eventId,
  field,
  timestamp,
  displayJsonFormat,
}) => {
  const { sensitiveFields } = useCapabilities();
  const { isRevealed, setRevealedTarget, toggleRevealed } =
    useRevealStateForTarget(`${eventType}:${eventId}:${field}`);

  const hasTarget = !!eventId;

  const {
    data: revealData,
    error: revealError,
    isLoading,
  } = useRevealEventField({
    env,
    eventType,
    eventId,
    field,
    timestamp,
    enabled: isRevealed && hasTarget,
  });

  useResetOnRevealError(revealError, setRevealedTarget);

  return (
    <SensitivePropertyFieldView
      displayJsonFormat={displayJsonFormat}
      isLoading={isLoading}
      isRevealable={hasTarget && sensitiveFields.canReveal}
      propertyName={propertyName}
      onToggle={toggleRevealed}
      {...revealedState(isRevealed, revealData, value)}
    />
  );
};
